import React from "react";
import { useTranslation } from "react-i18next";
import styles from "../CRMSettingsPage.module.css";

const RETAIL_MODULES = [
  {
    key: "shop",
    ru: "Интернет-магазин",
    en: "Online store",
    ruDescription: "Каталог, корзина, checkout, обычные заказы, доставка и оплата.",
    enDescription: "Catalog, cart, checkout, regular orders, delivery and payments.",
  },
  {
    key: "auction",
    ru: "Аукцион",
    en: "Auction",
    ruDescription: "Лоты, ставки, резервная цена, «Купить сейчас» и правила торгов.",
    enDescription: "Lots, bids, reserve prices, Buy now and auction rules.",
  },
  {
    key: "marketplace",
    ru: "Доска объявлений",
    en: "Marketplace",
    ruDescription: "Объявления бизнеса и клиентов, профили продавцов, сообщения и модерация.",
    enDescription: "Business and customer listings, seller profiles, messages and moderation.",
  },
];

export default function ModulesTab({
  academyEnabled,
  setAcademyEnabled,
  retailShopEnabled,
  setRetailShopEnabled,
  retailShopModules,
  setRetailShopModules,
}) {
  const { t, i18n } = useTranslation();
  const uiText = (ru, en) => String(i18n.resolvedLanguage || i18n.language || "ru").toLowerCase().startsWith("ru") ? ru : en;

  const modules = [
    {
      key: "academy",
      title: t("academy", { defaultValue: uiText("Академия", "Academy") }),
      description: t("modules_academy_description", {
        defaultValue: uiText(
          "Интерактивное обучение CRM: подсказки, пошаговые сценарии и практические workflow.",
          "Interactive CRM training with hints, guided tours and practical workflows."
        ),
      }),
      note: t("modules_academy_data_preserved", {
        defaultValue: uiText(
          "При отключении прогресс обучения и созданные данные сохраняются.",
          "Training progress and created data remain saved while the module is disabled."
        ),
      }),
      enabled: academyEnabled,
      setEnabled: setAcademyEnabled,
    },
    {
      key: "retail-shop",
      title: "RetailShop",
      description: uiText(
        "Публичная коммерческая витрина. После включения ниже можно выбрать, какие разделы нужны этому клиенту.",
        "Public commerce storefront. Once enabled, choose below which commerce sections this client needs."
      ),
      note: uiText(
        "Если RetailShop выключен, пункт «Интернет-магазин» скрывается из меню CRM. Настройки и данные не удаляются.",
        "When RetailShop is disabled, Online store is hidden from the CRM menu. Settings and data are preserved."
      ),
      enabled: retailShopEnabled,
      setEnabled: (next) => {
        setRetailShopEnabled(next);
        if (next && !Object.values(retailShopModules || {}).some(Boolean)) {
          setRetailShopModules({ shop: true, auction: false, marketplace: false });
        }
      },
    },
  ];

  const toggleRetailModule = (key) => {
    if (!retailShopEnabled) return;
    setRetailShopModules((current) => {
      const next = { ...(current || { shop: true, auction: false, marketplace: false }), [key]: !current?.[key] };
      // RetailShop must always have at least one public section while it is enabled.
      if (!next.shop && !next.auction && !next.marketplace) return current;
      return next;
    });
  };

  const activeRetailModules = RETAIL_MODULES.filter((item) => retailShopModules?.[item.key]);

  return (
    <section className={styles.modulesSettings}>
      <header className={styles.modulesHeader}>
        <div>
          <span className={styles.modulesEyebrow}>{t("modules_visibility", { defaultValue: uiText("Видимость функций", "Feature visibility") })}</span>
          <h2>{t("modules_crm", { defaultValue: uiText("Модули CRM", "CRM modules") })}</h2>
          <p>{t("modules_crm_description", { defaultValue: uiText("Включайте только те крупные разделы, которые нужны текущему клиенту CRM.", "Enable only the major modules required for the current CRM tenant.") })}</p>
        </div>
      </header>

      <div className={styles.modulesList}>
        {modules.map((module) => (
          <React.Fragment key={module.key}>
            <article className={`${styles.moduleCard} ${module.enabled ? styles.moduleCardEnabled : ""}`}>
              <div className={styles.moduleCopy}>
                <div className={styles.moduleTitleRow}>
                  <h3>{module.title}</h3>
                  <span className={`${styles.moduleStatus} ${module.enabled ? styles.moduleStatusEnabled : ""}`}>
                    {module.enabled ? t("enabled", { defaultValue: uiText("Включено", "Enabled") }) : t("disabled", { defaultValue: uiText("Выключено", "Disabled") })}
                  </span>
                </div>
                <p>{module.description}</p>
                <small>{module.note}</small>
              </div>

              <label className={styles.moduleSwitch}>
                <input
                  type="checkbox"
                  checked={module.enabled}
                  onChange={(event) => module.setEnabled(event.target.checked)}
                  aria-label={`${module.title}: ${module.enabled ? uiText("выключить", "disable") : uiText("включить", "enable")}`}
                />
                <span aria-hidden="true" />
              </label>
            </article>

            {module.key === "retail-shop" && (
              <div className={`${styles.retailModuleConfigurator} ${!retailShopEnabled ? styles.retailModuleConfiguratorDisabled : ""}`}>
                <div className={styles.retailModuleConfiguratorHeader}>
                  <div>
                    <h3>{uiText("Разделы RetailShop", "RetailShop sections")}</h3>
                    <p>{uiText(
                      "Выберите, какой функционал будет доступен на публичном сайте. После сохранения соответствующие разделы автоматически появятся в настройках «Интернет-магазин».",
                      "Choose which functionality is available on the public site. After saving, the matching sections automatically appear in Online store settings."
                    )}</p>
                  </div>
                  {!retailShopEnabled && <span className={styles.retailModuleLocked}>{uiText("Сначала включите RetailShop", "Enable RetailShop first")}</span>}
                </div>

                <div className={styles.retailModuleGrid}>
                  {RETAIL_MODULES.map((item) => {
                    const enabled = Boolean(retailShopModules?.[item.key]);
                    return (
                      <button
                        key={item.key}
                        type="button"
                        className={`${styles.retailModuleOption} ${enabled ? styles.retailModuleOptionActive : ""}`}
                        onClick={() => toggleRetailModule(item.key)}
                        disabled={!retailShopEnabled}
                        aria-pressed={enabled}
                      >
                        <span className={styles.retailModuleOptionTop}>
                          <strong>{uiText(item.ru, item.en)}</strong>
                          <span className={`${styles.retailModuleCheck} ${enabled ? styles.retailModuleCheckActive : ""}`} aria-hidden="true">
                            {enabled ? "✓" : ""}
                          </span>
                        </span>
                        <small>{uiText(item.ruDescription, item.enDescription)}</small>
                      </button>
                    );
                  })}
                </div>

                <div className={styles.retailModuleSummary}>
                  <div>
                    <strong>{uiText("Будет доступно", "Enabled sections")}</strong>
                    <small>{uiText("Эти разделы появятся в настройках RetailShop после сохранения.", "These sections appear in RetailShop settings after saving.")}</small>
                  </div>
                  <div className={styles.retailModuleSummaryItems}>
                    {activeRetailModules.map((item) => <span key={item.key}>{uiText(item.ru, item.en)}</span>)}
                  </div>
                </div>
              </div>
            )}
          </React.Fragment>
        ))}
      </div>

      <div className={styles.modulesNotice}>
        <span>{uiText(
          "Изменения применяются после «Сохранить изменения». Отключение модуля не удаляет связанные настройки или данные.",
          "Changes apply after Save changes. Disabling a module never deletes its settings or data."
        )}</span>
      </div>
    </section>
  );
}
